import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import {
  DS_TOOLTIP_PLACEMENTS,
  type DSTooltipOptions,
  type DSTooltipPlacement,
} from "@/src/design_system/primitives/tooltip/types/options";

interface UseDSTruncationTooltipOptions {
  elementRef: Ref<HTMLElement | null | undefined>;
  text: Ref<string | null | undefined>;
  placement?: DSTooltipPlacement;
  disabled?: Ref<boolean>;
}

export const useDSTruncationTooltip = ({ elementRef, text, placement, disabled }: UseDSTruncationTooltipOptions) => {
  const isTruncated = ref(false);
  let observedElement: HTMLElement | undefined;
  let resizeObserver: ResizeObserver | undefined;

  const observeElement = (element: HTMLElement | undefined) => {
    if (element === observedElement) {
      return;
    }

    resizeObserver?.disconnect();
    observedElement = element;

    if (element) {
      resizeObserver?.observe(element);
    }
  };

  const refresh = async () => {
    await nextTick();

    const element = elementRef.value ?? undefined;
    observeElement(element);
    isTruncated.value = Boolean(!disabled?.value && element && element.scrollWidth > element.clientWidth);
  };

  onMounted(() => {
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => void refresh());
    }

    void refresh();
  });

  watch([elementRef, text, () => disabled?.value], () => void refresh(), { flush: "post" });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  const tooltip = computed<DSTooltipOptions | false>(() => {
    const tooltipText = text.value ?? "";

    if (disabled?.value || !tooltipText || !isTruncated.value) {
      return false;
    }

    return {
      text: tooltipText,
      placement: placement ?? DS_TOOLTIP_PLACEMENTS.Bottom,
    };
  });

  return {
    tooltip,
    refresh,
  };
};
